<template>
  <main id="page-wrap" class="bf-detail">
    <div class="grid-container">
      <div class="grid-x align-center">
        <div class="cell small-12 medium-10 large-8">
          <router-link :to="{ name: 'bytesforslag' }" class="bf-back">
            &larr; {{ $t('swaps.title') }}
          </router-link>

          <div v-if="detailLoading && !detail" class="bf-loading">
            {{ $t('general.loading') }}…
          </div>

          <div v-else-if="!detail" class="bf-empty">
            <p>{{ $t('swaps.empty') }}</p>
          </div>

          <template v-else>
            <section class="bf-header">
              <div class="bf-header__photo">
                <img v-if="headerPhoto" :src="headerPhoto" :alt="movingTo.street">
              </div>
              <div class="bf-header__info">
                <h1>{{ movingTo.street }} {{ movingTo.street_number }}</h1>
                <p class="bf-header__hood">
                  <span v-if="movingTo.hood">{{ movingTo.hood }}, </span>{{ movingTo.city }}
                </p>
                <ul class="bf-header__stats">
                  <li v-if="movingTo.rooms != null">{{ movingTo.rooms }} {{ $t('ad.rooms') }}</li>
                  <li v-if="movingTo.size != null">{{ movingTo.size }} m²</li>
                  <li v-if="movingTo.rent != null">{{ movingTo.rent }} kr/{{ $t('ad.month') }}</li>
                </ul>
                <div class="bf-header__summary">
                  <div>
                    <strong>{{ detail.chain_count }}</strong>
                    <span>{{ $t('swaps.chains') }}</span>
                  </div>
                  <div>
                    <strong>{{ detail.best_progress_pct }}%</strong>
                    <span>{{ $t('swaps.progress') }}</span>
                  </div>
                </div>
              </div>
            </section>

            <h2 class="bf-chains-title">Kedjor</h2>
            <ul class="bf-chains">
              <li v-for="(chain, idx) in detail.chains" :key="idx" class="bf-chain">
                <div class="bf-chain__meta">
                  <span class="bf-chain__size">{{ sizeLabel(chain.swap_size) }}</span>
                  <div class="bf-chain__progress">
                    <div
                      class="bf-chain__progress-bar"
                      :style="{ width: chain.progress_pct + '%' }"
                    />
                  </div>
                  <span class="bf-chain__pct">{{ chain.progress_pct }}%</span>
                </div>

                <ChainDiagram :chain="chain" :label-map="labelMap" />
              </li>
            </ul>
          </template>
        </div>
      </div>
    </div>
  </main>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import { v2PhotoUrl } from '@/utils/v2Photo'
import { swapSizeLabel } from '@/utils/swapMeta'
import ChainDiagram from '@/components/ChainDiagram.vue'

export default {
  name: 'BytesforslagDetail',
  components: {
    ChainDiagram
  },
  props: {
    id: {
      type: String,
      required: true
    }
  },
  computed: {
    ...mapState('swaps', ['detail', 'detailLoading']),
    ...mapGetters(['myAds']),
    movingTo () {
      return (this.detail && this.detail.moving_to) || {}
    },
    headerPhoto () {
      return v2PhotoUrl(this.movingTo.photo)
    },
    labelMap () {
      const map = {}
      if (!this.detail) return map

      // Target ad (the one the user is considering)
      if (this.detail.to_ad_id) {
        map[this.detail.to_ad_id] = this.addressLabel(this.movingTo)
      }

      // Intermediate ads from via[] entries across all chains
      ;(this.detail.chains || []).forEach(chain => {
        ;(chain.via || []).forEach(via => {
          map[via.ad_id] = this.addressLabel(via)
        })
      })

      // User's own ad — look up by from_ad_id in myAds
      if (this.detail.from_ad_id) {
        const own = (this.myAds || []).find(a => a.id === this.detail.from_ad_id)
        map[this.detail.from_ad_id] = own
          ? this.addressLabel(own)
          : this.$t('swaps.my-home')
      }

      return map
    }
  },
  watch: {
    id: {
      immediate: true,
      handler (newId) {
        if (newId) {
          this.$store.dispatch('swaps/fetchSwapDetail', newId)
        }
      }
    }
  },
  methods: {
    addressLabel (ad) {
      if (!ad) return ''
      const street = ad.street || ''
      const num = ad.street_number || ''
      return (street + ' ' + num).trim()
    },
    sizeLabel (size) {
      return swapSizeLabel(size)
    }
  }
}
</script>

<style scoped>
.bf-detail {
  margin-top: 74px;
  padding-bottom: 40px;
}

.bf-back {
  display: inline-block;
  margin-bottom: 16px;
  color: #2a7a6d;
  text-decoration: none;
  font-size: 0.9rem;
}

.bf-back:hover {
  text-decoration: underline;
}

.bf-header {
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
  padding: 16px;
  background: #fff;
  border-radius: 6px;
  margin-bottom: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
}

@media (min-width: 640px) {
  .bf-header {
    grid-template-columns: 260px 1fr;
  }
}

.bf-header__photo {
  width: 100%;
  aspect-ratio: 4 / 3;
  overflow: hidden;
  border-radius: 4px;
  background: #eee;
}

.bf-header__photo img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.bf-header__info h1 {
  margin: 0 0 4px;
  font-size: 1.4rem;
}

.bf-header__hood {
  margin: 0 0 12px;
  color: #666;
}

.bf-header__stats {
  list-style: none;
  padding: 0;
  margin: 0 0 16px;
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  color: #333;
}

.bf-header__summary {
  display: flex;
  gap: 24px;
  padding-top: 12px;
  border-top: 1px solid #eee;
}

.bf-header__summary strong {
  display: block;
  font-size: 1.4rem;
  color: #2a7a6d;
}

.bf-header__summary span {
  font-size: 0.8rem;
  color: #777;
}

.bf-chains-title {
  margin: 0 0 12px;
}

.bf-chains {
  list-style: none;
  padding: 0;
  margin: 0;
}

.bf-chain {
  padding: 16px;
  margin-bottom: 12px;
  background: #fff;
  border-radius: 6px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
}

.bf-chain__meta {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;
}

.bf-chain__size {
  padding: 2px 8px;
  background: #e8f5f2;
  color: #2a7a6d;
  border-radius: 10px;
  font-size: 0.75rem;
  font-weight: 600;
}

.bf-chain__progress {
  flex: 1;
  height: 6px;
  background: #eee;
  border-radius: 3px;
  overflow: hidden;
}

.bf-chain__progress-bar {
  height: 100%;
  background: #64bcb2;
}

.bf-chain__pct {
  font-size: 0.8rem;
  color: #555;
  min-width: 36px;
  text-align: right;
}

.bf-loading,
.bf-empty {
  padding: 32px 16px;
  text-align: center;
  color: #666;
  background: #fff;
  border-radius: 6px;
}
</style>
